import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import { useSettings } from './SettingsContext';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const { user } = useAuth();
  const { settings, loading: settingsLoading } = useSettings();

  const canRead = settings?.my_permissions
    ? settings.my_permissions['notifications.read'] !== false
    : false;
  const canUpdate = settings?.my_permissions
    ? settings.my_permissions['notifications.update'] !== false
    : false;

  const fetchNotifications = async () => {
    if (!user) return;
    if (disabled) return;
    if (!canRead) return;
    if (settingsLoading) return;
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        setDisabled(true);
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      console.error('Failed to fetch notifications', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      if (!canUpdate) return;
      await api.put(`/notifications/${id}/read`);
      if (id === 'all') {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: 1 })));
        setUnreadCount(0);
      } else {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: 1 } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) return;
    }
  };

  useEffect(() => {
    if (user) {
      setDisabled(false);
      if (!canRead) return;
      if (settingsLoading) return;
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user, canRead, settingsLoading]);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markAsRead, fetchNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
};
